<!-- Receipt Modal Component -->
<template>
  <div v-if="showReceiptModal" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Overlay -->
    <div
      class="fixed inset-0 bg-black/50 backdrop-blur-sm"
      @click="closeModal"
    ></div>

    <!-- Modal Container -->
    <div class="flex min-h-full items-center justify-center p-4">
      <!-- Modal Content -->
      <div
        class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 transition-all"
      >
        <!-- Header -->
        <div
          class="bg-gradient-to-r from-[#2f9cf5]/80 to-[#2f9cf5]/10 p-6 text-white"
        >
          <div class="flex justify-between items-center">
            <h2 class="text-xl font-bold">Purchase Complete</h2>
            <button @click="closeModal" class="text-white/80 hover:text-white">
              <i class="fa-solid fa-xmark text-xl"></i>
            </button>
          </div>
          <p class="text-sm opacity-90 mt-1">{{ storeName }}</p>
        </div>

        <!-- Receipt Content -->
        <div class="p-6">
          <!-- Receipt Header -->
          <div class="text-center mb-6">
            <div
              class="w-16 h-16 bg-blue-100 dark:bg-[#2f9cf5]/60 rounded-full flex items-center justify-center mx-auto mb-3"
            >
              <i
                class="fa-solid fa-check text-blue-600 dark:text-blue-400 text-2xl"
              ></i>
            </div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">
              Thank You!
            </h3>
            <p class="text-gray-500 dark:text-gray-400 text-sm mt-1">
              Your purchase was successful
            </p>
          </div>

          <!-- Receipt Details -->
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden dark:text-gray-200 text-gray-600"
          >
            <!-- Receipt Header -->
            <div
              class="bg-gray-50 dark:bg-gray-800 p-4 border-b border-gray-200 dark:border-gray-700"
            >
              <div class="flex justify-between">
                <div>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Order #
                  </p>
                  <p class="text-sm font-medium">{{ orderCode }}</p>
                </div>
                <div class="text-right">
                  <p class="text-xs text-gray-500 dark:text-gray-400">Date</p>
                  <p class="text-sm font-medium">
                    {{ new Date().toLocaleDateString() }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Items List -->
            <div
              class="divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto"
            >
              <div v-for="item in receiptItems" :key="item.id" class="p-4">
                <div class="flex justify-between">
                  <div>
                    <p class="font-medium text-gray-900 dark:text-white">
                      {{ item.name }}
                    </p>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ item.quantity }} ×
                      {{ formatCurrency(Number(item.cost)) }}
                    </p>
                  </div>
                  <p class="font-medium">
                    {{ formatCurrency(Number(item.cost) * item.quantity) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Receipt Footer -->
            <div
              class="bg-gray-50 dark:bg-gray-800 p-4 border-t border-gray-200 dark:border-gray-700"
            >
              <div class="space-y-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Subtotal</span>
                  <span>{{ formatCurrency(Number(receiptSubtotal)) }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600 dark:text-gray-400">Tax</span>
                  <span>{{ formatCurrency(Number(receiptTax)) }}</span>
                </div>
                <div
                  class="flex justify-between font-bold text-lg pt-2 border-t border-gray-200 dark:border-gray-700 mt-2"
                >
                  <span>Total</span>
                  <span>{{ formatCurrency(Number(receiptTotal)) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Method -->
          <div
            class="mt-4 flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-gray-800 dark:text-gray-200"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center"
              >
                <i
                  :class="paymentMethodIcon"
                  class="text-blue-600 dark:text-blue-400"
                ></i>
              </div>
              <span class="text-sm font-medium"
                >Paid with {{ selectedPaymentMethod }}</span
              >
            </div>
            <span class="text-sm text-green-600 dark:text-green-400 font-medium"
              >Paid</span
            >
          </div>
        </div>

        <!-- Action Buttons -->
        <div
          class="p-6 pt-0 grid grid-cols-3 gap-3 text-gray-800 dark:text-gray-200"
        >
          <button
            @click="printReceipt"
            class="flex flex-col items-center justify-center p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <i
              class="fa-solid fa-print text-gray-700 dark:text-gray-300 text-lg mb-1"
            ></i>
            <span class="text-xs">Print</span>
          </button>
          <button
            @click="emailReceipt"
            class="flex flex-col items-center justify-center p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <i
              class="fa-solid fa-envelope text-gray-700 dark:text-gray-300 text-lg mb-1"
            ></i>
            <span class="text-xs">Email</span>
          </button>
          <button
            @click="whatsappReceipt"
            class="flex flex-col items-center justify-center p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <i
              class="fa-brands fa-whatsapp text-gray-700 dark:text-gray-300 text-lg mb-1"
            ></i>
            <span class="text-xs">WhatsApp</span>
          </button>
        </div>

        <!-- Done Button -->
        <div class="px-6 pb-6">
          <button
            @click="closeModal"
            class="w-full py-3 bg-gray-900 dark:bg-gray-100 hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-gray-900 rounded-lg font-bold transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    showReceiptModal: Boolean,
    receiptItems: Array,
    receiptSubtotal: Number,
    receiptTax: Number,
    receiptTotal: Number,
    selectedPaymentMethod: String,
    storeName: String,
    currencySymbol: String,
    orderCode: String,
  },
  computed: {
    paymentMethodIcon() {
      const method = this.selectedPaymentMethod.toLowerCase();
      if (method.includes("card")) return "fa-regular fa-credit-card";
      if (method.includes("transfer")) return "fa-solid fa-mobile-screen";
      if (method.includes("cash")) return "fa-solid fa-money-bill-wave";
      return "fa-solid fa-wallet";
    },
  },
  watch: {
    showReceiptModal(newVal) {
      if (newVal) {
        console.log("Recipt items from the posview:", this.receiptTotal);
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    formatCurrency(amount) {
      return `${this.currencySymbol}${amount.toFixed(2)}`;
    },
    printReceipt() {
      // Create a print-friendly version
      const printContent = `
        <div style="font-family: Arial, sans-serif; max-width: 300px; margin: 0 auto; padding: 20px;">
          <h2 style="text-align: center; margin-bottom: 5px;">${this.storeName}</h2>
          <p style="text-align: center; font-size: 12px; color: #666; margin-bottom: 20px;">${new Date().toLocaleString()}</p>
          <p style="text-align: center; font-size: 11px; margin-bottom: 20px;">Order #${this.orderCode}</p>
          
          <div style="border-top: 1px dashed #ccc; margin: 15px 0;"></div>
          
          ${this.receiptItems
            .map(
              (item) => `
            <div style="margin-bottom: 10px;">
              <div style="display: flex; justify-content: space-between;">
                <span style="font-weight: bold;">${item.name}</span>
                <span>${this.formatCurrency(Number(item.cost) * item.quantity)}</span>
              </div>
              <div style="font-size: 12px; color: #666;">
                ${item.quantity} × ${this.formatCurrency(Number(item.cost))}
              </div>
            </div>
          `
            )
            .join("")}
          
          <div style="border-top: 1px dashed #ccc; margin: 15px 0;"></div>
          
          <div style="margin-bottom: 5px; display: flex; justify-content: space-between;">
            <span>Subtotal:</span>
            <span>${this.formatCurrency(Number(this.receiptSubtotal))}</span>
          </div>
          <div style="margin-bottom: 5px; display: flex; justify-content: space-between;">
            <span>Tax:</span>
            <span>${this.formatCurrency(Number(this.receiptTax))}</span>
          </div>
          <div style="margin-top: 10px; font-weight: bold; display: flex; justify-content: space-between;">
            <span>TOTAL:</span>
            <span>${this.formatCurrency(Number(this.receiptTotal))}</span>
          </div>
          
          <div style="border-top: 1px dashed #ccc; margin: 15px 0;"></div>
          
          <p style="text-align: center; font-size: 12px; margin-top: 20px;">
            Thank you for your purchase!
          </p>
        </div>
      `;

      const printWindow = window.open("", "_blank");
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    },
    emailReceipt() {
      const subject = `Your Receipt from ${this.storeName}`;
      const body = `
        Thank you for your purchase!\n\n
        Order #${this.orderCode}\n
        Date: ${new Date().toLocaleString()}\n\n
        ${this.receiptItems
          .map(
            (item) => `
          ${item.name} - ${item.quantity} × ${this.formatCurrency(Number(item.cost))} = ${this.formatCurrency(Number(item.cost) * item.quantity)}
        `
          )
          .join("\n")}\n\n
        Subtotal: ${this.formatCurrency(Number(this.receiptSubtotal))}\n
        Tax: ${this.formatCurrency(Number(this.receiptTax))}\n
        TOTAL: ${this.formatCurrency(Number(this.receiptTotal))}\n\n
        Payment Method: ${this.selectedPaymentMethod}\n\n
        Please contact us if you have any questions.
      `;

      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    },
    whatsappReceipt() {
      const message = `
        *Receipt from ${this.storeName}*\n
        Order #${this.orderCode}\n
        Date: ${new Date().toLocaleString()}\n\n
        ${this.receiptItems
          .map(
            (item) => `
          ${item.name} - ${item.quantity} × ${this.formatCurrency(Number(item.cost))} = ${this.formatCurrency(Number(item.cost) * item.quantity)}
        `
          )
          .join("\n")}\n\n
        *Subtotal*: ${this.formatCurrency(Number(this.receiptSubtotal))}\n
        *Tax*: ${this.formatCurrency(Number(this.receiptTax))}\n
        *TOTAL*: ${this.formatCurrency(Number(this.receiptTotal))}\n\n
        Payment Method: ${this.selectedPaymentMethod}\n\n
        Thank you for your purchase!
      `;

      window.open(
        `https://wa.me/?text=${encodeURIComponent(message)}`,
        "_blank"
      );
    },
  },
};
</script>

<style scoped>
/* Add any custom styles here */
</style>
